/*
 * Decléor — open a Klaviyo signup form from anywhere in the theme
 * ---------------------------------------------------------------------------
 * Any element carrying `data-kl-open="<form id>"` opens that Klaviyo form when clicked.
 * Two consumers today: a slide of the product page's reassurance slider
 * (snippets/product-reassurance.liquid) and a block of the announcement bar
 * (sections/announcement.liquid). Both used to be impossible without an app block.
 *
 * WHY THE FORM IS OPENED THROUGH klaviyo.openForm AND NOT BY CLICKING KLAVIYO'S TEASER.
 * The teaser is the obvious route and it is wrong: it is not rendered on every page (measured:
 * `[class*="kl-teaser"]` matches nothing on a product page), so on most of the store there
 * would be nothing to click. `window.klaviyo.openForm` is Klaviyo's documented API and is a
 * real function here (measured: window.klaviyo exposes push / openForm / identify / track / …).
 *
 * `_klOnsite` is the pre-boot path, not a second implementation: it is Klaviyo's own queue,
 * drained once their bundle has loaded, so a click in the first moments of a page still opens
 * the form instead of doing nothing.
 *
 * DELEGATED ON document. The product section is morphed (`data-morph-children-only`,
 * `x-data="productForm()"`) and the announcement bar is an Alpine ticker that clones and
 * re-renders its slides — both replace nodes, which drops any listener held on the old one.
 * Delegation outlives every rebuild and needs no re-binding on section reload.
 *
 * KEYBOARD. A consumer may be a <button>, in which case Enter and Space are handled by the
 * browser and the keydown branch below never fires (a <button> already emits a click for
 * both). Where the marker sits on a non-button — the announcement bar's block is a <div> that
 * cannot be a <button> without restructuring its ticker — the element carries
 * role="button" tabindex="0" and this file supplies the two keys, which is what makes that
 * pattern accessible rather than merely clickable.
 *
 * A NOTE ON VERIFYING THIS. Do not try to spy on klaviyo.openForm: `window.klaviyo` is a Proxy
 * that silently swallows property writes (measured — the descriptor reports writable and
 * configurable true, the object is neither frozen nor sealed, and yet assigning openForm
 * leaves the original function in place). An assignment-based spy records nothing and looks
 * exactly like a dead handler. Assert on the dialog instead: a real form id makes
 * `[data-kl-scroll-locking-modal]` appear (verified with St7E8k — 0 visible dialogs before the
 * click, one 1000px "POPUP Form" after).
 *
 * No-op on any page with no [data-kl-open].
 */
(function () {
  'use strict';

  var SEL = '[data-kl-open]';

  function open(el) {
    var id = el.getAttribute('data-kl-open');
    if (!id) {
      /* No id means the merchant ticked the option but left the form field empty. Do nothing
         rather than call openForm() with nothing, which throws inside Klaviyo's bundle. */
      return;
    }
    if (window.klaviyo && typeof window.klaviyo.openForm === 'function') {
      window.klaviyo.openForm(id);
      return;
    }
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', id]);
  }

  function target(event) {
    return event.target && event.target.closest ? event.target.closest(SEL) : null;
  }

  document.addEventListener('click', function (event) {
    var el = target(event);
    if (el) {
      open(el);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    var el = target(event);
    /* A real <button> already turns both keys into a click; handling them again here would
       open the form twice. */
    if (!el || el.tagName === 'BUTTON' || el.tagName === 'A') {
      return;
    }
    event.preventDefault(); // Space would otherwise scroll the page
    open(el);
  });
})();
